import { Plus, Trash2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const InvoiceLineItems = ({
  lineItems,
  isEditing,
  updateLineItem,
  addLineItem,
  removeLineItem
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
            Rental Items & Services
          </h3>
        </div>
        {isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={addLineItem}
            className="gap-2 text-primary border-primary/30 hover:bg-primary/5"
          >
            <Plus className="w-4 h-4" />
            Add Item
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="invoice-table-header hover:bg-invoice-table-header">
              <TableHead className="font-semibold text-foreground w-[50%]">
                Description
              </TableHead>
              <TableHead className="font-semibold text-foreground text-right w-[15%]">
                Unit Price
              </TableHead>
              <TableHead className="font-semibold text-foreground text-center w-[15%]">
                Qty/Days
              </TableHead>
              <TableHead className="font-semibold text-foreground text-right w-[15%]">
                Amount
              </TableHead>
              {isEditing && (
                <TableHead className="w-[5%]"></TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((item, index) => (
              <TableRow
                key={item.id}
                className={index % 2 === 1 ? 'bg-invoice-table-stripe' : ''}
              >
                <TableCell className="font-medium">
                  {isEditing ? (
                    <Input
                      value={item.description}
                      onChange={(e) =>
                        updateLineItem(item.id, "description", e.target.value)
                      }
                      className="h-8 text-sm"
                    />
                  ) : (
                    item.description
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {isEditing ? (
                    <Input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) =>
                        updateLineItem(
                          item.id,
                          "unitPrice",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="h-8 text-sm text-right w-24 ml-auto"
                      min="0"
                      step="0.01"
                    />
                  ) : (
                    formatCurrency(item.unitPrice)
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {isEditing ? (
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateLineItem(
                          item.id,
                          "quantity",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="h-8 text-sm text-center w-20 mx-auto"
                      min="0"
                    />
                  ) : (
                    item.quantity
                  )}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(item.unitPrice * item.quantity)}
                </TableCell>
                {isEditing && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLineItem(item.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}

            {lineItems.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={isEditing ? 5 : 4}
                  className="text-center py-8 text-muted-foreground"
                >
                  No items added yet. Click &quot;Add Item&quot; to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
